import React, { useCallback, useState } from "react";
import WorkTimeTypeDrawer from "../components/worktime/workTimeTypeDrawer";
import { useAppDispatch, useAppSelector } from "../framework/redux/hooks";
import { handleApiError } from "../services/baseService";
import { LoadingIndicator } from "../components/framework/loadingIndicator";
import { MainButtons } from "./mainButtons";
import { MainPageTitle } from "./mainPageTitle";
import { useTranslation } from "react-i18next";
import { WorkTimeCurrentDetails } from "../components/worktime/workTimeCurrentDetails";
import { useSelectCurrentWorkShiftTimeSlot } from "../hooks/worktime/useSelectWorkShiftTimeSlots";
import { MainLayout } from "../components/layout/mainLayout";
import { Box } from "@mui/material";
import { useResume } from "../hooks/framework/useResume";
import { useAsyncThunkAction } from "../hooks/framework/useAsyncThunkAction";
import { fetchCurrentWorkShift } from "../framework/redux/slices/workShiftSlice";
import { useEventListener, useInterval } from "usehooks-ts";
import { TransportList } from "../components/transport/transportList";
import { useAppOptionValueEquals } from "../hooks/framework/useAppOptionValue";
import { optionOwnerHasEasyOppTelematicsEnabled, optionOwnerHasTransportEnabled, OptionWorkShiftPlanningEnabled } from "../models/common/consts";
import { Base } from "../framework/common/base";
import { fetchPlannedWorkShifts } from "../framework/redux/slices/plannedWorkShiftSlice";
import { useSelectPlannedWorkShiftsByTimeRange } from "../hooks/worktime/useSelectPlannedWorkShifts";
import { PlannedWorkShiftCard } from "../components/worklog/PlannedWorkShiftCard";
import { DailyWorklogTitle } from "../components/worklog/dailyWorklogTitle";
import { useShowWorkShiftEndTimesInMobile } from "../hooks/framework/useShowWorkShiftEndTimesInMobile";
import { fetchVehicleMaintenancesForVehicle } from "../framework/redux/slices/vehicleMaintenanceSlice";

const workShiftFetchInterval = 5 * 60 * 1000; // 5 minutes

export const Main = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const user = useAppSelector(({ session }) => session.fullName);
    const current = useSelectCurrentWorkShiftTimeSlot();
    const dateString = Base.dateToDateJSON(new Date());
    const showEndTimes = useShowWorkShiftEndTimesInMobile();

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const [transportEnabled] = useAppOptionValueEquals(
        optionOwnerHasTransportEnabled,
        true
    );

    const [plannedShiftsEnabled] = useAppOptionValueEquals(
        OptionWorkShiftPlanningEnabled,
        true
    );

    const [telematicsEnabled] = useAppOptionValueEquals(
        optionOwnerHasEasyOppTelematicsEnabled,
        true
    );

    const plannedShifts = useSelectPlannedWorkShiftsByTimeRange(
        dateString,
        dateString
    );
    useAsyncThunkAction(
        () => fetchCurrentWorkShift(),
        { onError: handleApiError, onSuccess: () => setLoading(false) },
        []
    );

    useAsyncThunkAction(
        () => {
            if (telematicsEnabled && current?.vehicleId) {
                return fetchVehicleMaintenancesForVehicle(current.vehicleId);
            }

            return null;
        },
        { onError: handleApiError },
        [current?.vehicleId, telematicsEnabled]
    );

    useAsyncThunkAction(
        () =>
            plannedShiftsEnabled
                ? fetchPlannedWorkShifts({
                    // Fetch shifts from yesterday to tomorrow,
                    // in case there is an overnight shift which overlaps with today.
                    startDate: Base.dateToDateJSON(
                        Base.dateAddDays(dateString, -1)
                    ),
                    endDate: Base.dateToDateJSON(
                        Base.dateAddDays(dateString, 1)
                    ),
                })
                : null,
        { onError: handleApiError },
        [dateString, plannedShiftsEnabled]
    );

    const resumeCallback = useCallback(() => {
        dispatch(fetchCurrentWorkShift())
            .then(() => {
                setLoading(false);
            })
            .catch(handleApiError);
    }, []);

    // Fetch shift when
    useResume(resumeCallback); // 1. User becomes active
    useEventListener("online", resumeCallback); // 2. App is back online
    useInterval(resumeCallback, workShiftFetchInterval); // 3. Every 5 minutes

    if (loading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <MainLayout
                topComponent={
                    <MainPageTitle header={t("common.greeting")} text={user} />
                }
            >
                <Box mx={2}>
                    <WorkTimeCurrentDetails current={current} />
                    <MainButtons current={current} />
                </Box>

                {plannedShiftsEnabled && plannedShifts?.length > 0 ? (
                    <Box mx={2} mt={2}>
                        <>
                            {plannedShifts.length === 1
                                ? <DailyWorklogTitle
                                        title={t("workTime.workShift")}
                                        minutes={showEndTimes ? plannedShifts[0].duration : 0}
                                  />
                                : <DailyWorklogTitle
                                        title={t("workTime.workShifts")}
                                        minutes={showEndTimes ? plannedShifts.reduce((sum, shift) => sum + shift.duration, 0) : 0}
                                  />
                                                }
                            {[...plannedShifts]
                                .sort((a, b) => Base.dateCompare(a.startTime, b.startTime))
                                .map((s) => (
                                    <PlannedWorkShiftCard key={s.id} shift={s} />
                                ))}
                        </>
                    </Box>
                ) : null}

                {transportEnabled && (
                    <Box mt={2}>
                        <TransportList title={t("transport.orders.header.title")}/>
                    </Box>
                )}

                <Box mt={12} />
            </MainLayout>

            <WorkTimeTypeDrawer />
        </>
    );
};
